import { Building2, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LanguageSwitcher } from '@/components/shared/LanguageSwitcher';
import { ThemeSwitcher } from '@/components/shared/ThemeSwitcher';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useSidebar } from '@/contexts/SidebarContext';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';

export function Header(): JSX.Element {
  const { user } = useAuth();
  const { closeSidebar } = useSidebar();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeTenant, clearActiveTenant } = useTenantSwitcher();

  const handleReturnToTenants = (): void => {
    clearActiveTenant();
    navigate('/tenants');
  };

  const showTenantBanner = Boolean(activeTenant);

  return (
    <header className="flex h-16 items-center justify-between border-b bg-card px-6 shadow-sm">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={closeSidebar}
          aria-label={t('header.toggleNavigation')}
        >
          <Menu className="h-5 w-5" />
        </Button>
        {showTenantBanner && (
          <div className="flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-1.5 dark:border-border dark:bg-background">
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6 border border-primary/20">
                {activeTenant?.profileImage ? (
                  <AvatarImage
                    src={activeTenant.profileImage}
                    alt={activeTenant.name}
                  />
                ) : null}
                <AvatarFallback className="bg-primary/10 text-primary text-xs">
                  <Building2 className="h-3 w-3" />
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-muted-foreground">
                  {activeTenant?.name ?? ''}
                </span>
              </div>
            </div>
            {user?.role === 'root' && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs hover:bg-primary/10"
                onClick={handleReturnToTenants}
              >
                <X className="mr-1 h-3 w-3" />
                {t('header.backToTenants')}
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <ThemeSwitcher />
        <LanguageSwitcher />
      </div>
    </header>
  );
}
